/**
 * ToolsList Component
 *
 * Display categorized list of tools (builtin toolkits or custom tools)
 * Supports expandable sub-tools for builtin toolkits
 */

import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ToolItem } from "./ToolItem";
import { useI18n } from "@/i18n";
import type { BuiltinToolkit, CustomToolGroup } from "../../../types/tools";

interface ToolsListProps {
  type: "builtin" | "custom";
  builtinToolkits?: BuiltinToolkit[];
  customToolGroups?: CustomToolGroup[];
  isModuleSelected?: (moduleName: string) => boolean;
  isToolEnabled?: (toolName: string) => boolean;
  isSubToolEnabled?: (fullName: string) => boolean;
  isCategorySelected?: (category: string) => boolean;
  onToggleModule?: (moduleName: string) => void;
  onToggleTool?: (toolName: string) => void;
  onToggleSubTool?: (fullName: string, toolkitName: string) => void;
  onToggleCategory?: (category: string) => void;
  getDisplayName?: (toolName: string) => string; // Enhancement helper
}

export const ToolsList: React.FC<ToolsListProps> = ({
  type,
  builtinToolkits = [],
  customToolGroups = [],
  isModuleSelected = () => false,
  isToolEnabled = () => false,
  isSubToolEnabled = () => false,
  isCategorySelected = () => false,
  onToggleModule = () => {},
  onToggleTool = () => {},
  onToggleSubTool = () => {},
  onToggleCategory = () => {},
  getDisplayName,
}) => {
  // Track expanded toolkits
  const [expandedToolkits, setExpandedToolkits] = useState<Set<string>>(new Set());

  const toggleExpanded = (toolkitName: string) => {
    setExpandedToolkits((prev) => {
      const next = new Set(prev);
      if (next.has(toolkitName)) {
        next.delete(toolkitName);
      } else {
        next.add(toolkitName);
      }
      return next;
    });
  };
  const { messages } = useI18n();
  const t = messages.agentConfig.toolsBrowser as
    | Record<string, string>
    | undefined;

  if (type === "builtin") {
    if (builtinToolkits.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 text-sm">
          {t?.noBuiltinToolkits || "No builtin toolkits available"}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {builtinToolkits.map((toolkit) => {
          const isExpanded = expandedToolkits.has(toolkit.name);
          const hasTools = toolkit.tools && toolkit.tools.length > 0;

          return (
            <div
              key={toolkit.name}
              className={`border rounded-lg p-4 ${
                isModuleSelected(toolkit.name)
                  ? "border-blue-300 bg-blue-50"
                  : "border-gray-200"
              }`}
            >
              {/* Toolkit header */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={isModuleSelected(toolkit.name)}
                  onChange={() => onToggleModule(toolkit.name)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">
                      {toolkit.display_name || toolkit.name}
                    </h4>
                    {/* Expand/collapse button */}
                    {hasTools && (
                      <button
                        onClick={() => toggleExpanded(toolkit.name)}
                        className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                        title={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                  {/* Show technical name if display_name is different */}
                  {toolkit.display_name &&
                    toolkit.display_name !== toolkit.name && (
                      <span className="text-xs text-gray-400">
                        {toolkit.name}
                      </span>
                    )}
                  {toolkit.description && (
                    <p className="text-sm text-gray-600 mt-1">
                      {toolkit.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                      {toolkit.category}
                    </span>
                    <span>
                      {(t?.toolsCount || "{count} tools").replace(
                        "{count}",
                        String(toolkit.tool_count || 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Expandable sub-tools list */}
              {isExpanded && hasTools && (
                <div className="space-y-2 mt-4 ml-7 pl-4 border-l-2 border-gray-200">
                  {toolkit.tools!.map((tool) => (
                    <ToolItem
                      key={tool.full_name}
                      name={tool.name}
                      displayName={tool.display_name || getDisplayName?.(tool.full_name)}
                      description={tool.description}
                      enabled={isSubToolEnabled(tool.full_name)}
                      onToggle={() => onToggleSubTool(tool.full_name, toolkit.name)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Custom tools
  if (customToolGroups.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        {t?.noCustomTools || "No custom tools available"}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {customToolGroups.map((group) => (
        <div
          key={group.category}
          className="border border-gray-200 rounded-lg p-4"
        >
          {/* Category header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isCategorySelected(group.category)}
                onChange={() => onToggleCategory(group.category)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <h4 className="font-semibold text-gray-900">{group.category}</h4>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                {(t?.toolsCount || "{count} tools").replace(
                  "{count}",
                  String(group.tool_count)
                )}
              </span>
            </div>
            <span className="text-xs text-gray-500">{group.module}</span>
          </div>

          {/* Tools list */}
          <div className="space-y-2 mt-3">
            {group.tools.map((tool) => (
              <ToolItem
                key={tool.name}
                name={tool.name}
                displayName={tool.display_name || getDisplayName?.(tool.name)}
                description={tool.description}
                enabled={isToolEnabled(tool.name)}
                onToggle={() => onToggleTool(tool.name)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
